import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  apiClient as defaultClient,
  type ApiClient,
  type CheckChallengeResult,
  type DomainVerificationStatusResult,
  type IssueChallengeResult,
} from '../api/client';
import { AppShell } from '../components/AppShell';
import { Card } from '../components/Card';
import { Spinner } from '../components/Spinner';
import { Button } from '../components/Button';
import { InlineAlert } from '../components/InlineAlert';
import { StatusBadge } from '../components/StatusBadge';
import { HelpTip } from '../components/HelpTip';
import { useOrg } from '../org/context';
import { useFocusMain } from '../a11y/useFocusMain';
import { toDisplayError } from '../api/errorMessages';
import { AuthedBrand } from './AuthedBrand';
import styles from './DomainVerification.module.css';

type LoadState = 'loading' | 'error' | 'ready';

/** The four states the UI renders once loaded. */
type View = 'unverified' | 'pending' | 'verified' | 'expired';

export interface DomainVerificationProps {
  client?: ApiClient;
}

/** Human sentence for the polling cadence returned when a challenge is issued. */
function cadenceText(pollIntervalSeconds: number, maxWaitSeconds: number): string {
  const minutes = Math.round(maxWaitSeconds / 60);
  return `We check every ${pollIntervalSeconds} seconds for up to ${minutes} minutes.`;
}

function formatTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString();
}

export function DomainVerification({ client = defaultClient }: DomainVerificationProps) {
  const { currentOrg } = useOrg();
  const { id } = useParams<{ id: string }>();
  useFocusMain();

  const orgId = currentOrg?.id;

  const [phase, setPhase] = useState<LoadState>('loading');
  const [loadMessage, setLoadMessage] = useState('');
  const [status, setStatus] = useState<DomainVerificationStatusResult | null>(null);
  // The plaintext token + proof values are returned ONLY by issue — held here so
  // the pending panel can show them until the page is reloaded.
  const [issued, setIssued] = useState<IssueChallengeResult | null>(null);
  const [checkResult, setCheckResult] = useState<CheckChallengeResult | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [issuing, setIssuing] = useState(false);
  const [checking, setChecking] = useState(false);

  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (actionError) errorRef.current?.focus();
  }, [actionError]);

  const load = useCallback(async () => {
    if (!orgId || !id) return;
    setPhase('loading');
    try {
      const result = await client.getDomainVerification(orgId, id);
      setStatus(result);
      setPhase('ready');
    } catch (err) {
      setLoadMessage(toDisplayError(err).message);
      setPhase('error');
    }
  }, [client, orgId, id]);

  useEffect(() => {
    void load();
  }, [load]);

  const issue = useCallback(async () => {
    if (!orgId || !id || issuing) return;
    setIssuing(true);
    setActionError(null);
    try {
      const result = await client.issueDomainVerification(orgId, id);
      setIssued(result);
      setCheckResult(null);
      setStatus((prev) =>
        prev ? { ...prev, domainVerificationStatus: 'pending' } : prev,
      );
    } catch (err) {
      const display = toDisplayError(err);
      setActionError(display.message);
      // An already-verified domain (409) means the state moved under us — reflect it.
      if (
        err &&
        typeof err === 'object' &&
        'code' in err &&
        (err as { code: unknown }).code === 'conflict'
      ) {
        setIssued(null);
        setStatus((prev) =>
          prev ? { ...prev, domainVerificationStatus: 'verified' } : prev,
        );
      }
    } finally {
      setIssuing(false);
    }
  }, [client, orgId, id, issuing]);

  const check = useCallback(async () => {
    if (!orgId || !id || checking) return;
    setChecking(true);
    setActionError(null);
    try {
      const result = await client.checkDomainVerification(orgId, id);
      setCheckResult(result);
      if (result.status === 'verified') {
        setStatus((prev) =>
          prev ? { ...prev, domainVerificationStatus: 'verified' } : prev,
        );
      }
    } catch (err) {
      setActionError(toDisplayError(err).message);
    } finally {
      setChecking(false);
    }
  }, [client, orgId, id, checking]);

  return (
    <AppShell brand={<AuthedBrand />}>
      <Card>
        <h1 className={styles.heading}>Domain verification</h1>
        <p className={styles.subheading}>
          Prove you control this domain before we scan it.{' '}
          <HelpTip term="domainVerification" />
        </p>

        {phase === 'loading' ? (
          <div className={styles.loading}>
            <Spinner />
            <span>Loading verification status…</span>
          </div>
        ) : null}

        {phase === 'error' ? (
          <div className={styles.loadError}>
            <InlineAlert severity="error">{loadMessage}</InlineAlert>
            <Link to="/" className={styles.backLink}>
              Back to home
            </Link>
          </div>
        ) : null}

        {phase === 'ready' && status ? (
          <ReadyView
            status={status}
            projectId={id ?? ''}
            issued={issued}
            checkResult={checkResult}
            actionError={actionError}
            errorRef={errorRef}
            issuing={issuing}
            checking={checking}
            onIssue={issue}
            onCheck={check}
          />
        ) : null}
      </Card>
    </AppShell>
  );
}

interface ReadyViewProps {
  status: DomainVerificationStatusResult;
  projectId: string;
  issued: IssueChallengeResult | null;
  checkResult: CheckChallengeResult | null;
  actionError: string | null;
  errorRef: React.RefObject<HTMLDivElement>;
  issuing: boolean;
  checking: boolean;
  onIssue: () => void;
  onCheck: () => void;
}

function ReadyView({
  status,
  projectId,
  issued,
  checkResult,
  actionError,
  errorRef,
  issuing,
  checking,
  onIssue,
  onCheck,
}: ReadyViewProps) {
  const challengeStatus = checkResult?.status ?? status.challenge?.status ?? null;

  let view: View;
  if (status.domainVerificationStatus === 'verified' || checkResult?.status === 'verified') {
    view = 'verified';
  } else if (issued) {
    // A freshly-issued challenge is always the live pending one, even if the GET
    // that loaded the page reported an older expired challenge.
    view = 'pending';
  } else if (challengeStatus === 'expired') {
    view = 'expired';
  } else if (status.domainVerificationStatus === 'pending') {
    view = 'pending';
  } else {
    view = 'unverified';
  }

  const failureReason = checkResult?.failureReason ?? status.challenge?.failureReason ?? null;
  const lastChecked = formatTime(checkResult?.lastCheckedAt ?? status.challenge?.lastCheckedAt);

  const recordName = issued?.dnsTxt.recordName ?? status.challenge?.dnsTxt.recordName ?? '';
  const fileUrl = issued?.httpsFile.url ?? status.challenge?.httpsFile.url ?? '';

  return (
    <div>
      <div className={styles.badgeRow}>
        <span className={styles.badgeLabel}>Status:</span>
        <StatusBadge status={status.domainVerificationStatus} />
      </div>

      <InlineAlert ref={errorRef} severity="error">
        {actionError}
      </InlineAlert>

      {view === 'verified' ? (
        <div className={styles.section}>
          <InlineAlert severity="success">
            This domain is verified. Scanning is unlocked.
          </InlineAlert>
          <div className={styles.actions}>
            <Link to={`/projects/${projectId}/scans`}>View scans</Link>
          </div>
        </div>
      ) : null}

      {view === 'unverified' ? (
        <div className={styles.section}>
          <p className={styles.body}>
            We require domain ownership before the first scan of a domain. Start
            verification to receive a DNS record and a file you can publish to
            prove control.
          </p>
          <div className={styles.actions}>
            <Button busy={issuing} onClick={onIssue}>
              {issuing ? 'Starting…' : 'Start verification'}
            </Button>
          </div>
        </div>
      ) : null}

      {view === 'expired' ? (
        <div className={styles.section}>
          <InlineAlert severity="warning">
            This verification challenge expired. Re-request a fresh challenge to
            try again.
          </InlineAlert>
          <div className={styles.actions}>
            <Button busy={issuing} onClick={onIssue}>
              {issuing ? 'Re-requesting…' : 'Re-request'}
            </Button>
          </div>
        </div>
      ) : null}

      {view === 'pending' ? (
        <PendingPanel
          issued={issued}
          recordName={recordName}
          fileUrl={fileUrl}
          failureReason={failureReason}
          lastChecked={lastChecked}
          issuing={issuing}
          checking={checking}
          onIssue={onIssue}
          onCheck={onCheck}
        />
      ) : null}
    </div>
  );
}

interface PendingPanelProps {
  issued: IssueChallengeResult | null;
  recordName: string;
  fileUrl: string;
  failureReason: string | null;
  lastChecked: string | null;
  issuing: boolean;
  checking: boolean;
  onIssue: () => void;
  onCheck: () => void;
}

function PendingPanel({
  issued,
  recordName,
  fileUrl,
  failureReason,
  lastChecked,
  issuing,
  checking,
  onIssue,
  onCheck,
}: PendingPanelProps) {
  return (
    <div className={styles.section}>
      <p className={styles.body}>
        {issued
          ? cadenceText(issued.pollIntervalSeconds, issued.maxWaitSeconds)
          : 'We periodically re-check your domain until it verifies or the challenge expires.'}
      </p>

      {issued ? (
        <ReadOnlyField
          id="dv-token"
          label="Verification token"
          value={issued.token}
          help="Shown once. Copy it now — it will not be shown again after you leave."
        />
      ) : (
        <InlineAlert severity="info">
          The verification token is shown only once, when a challenge is issued.
          Re-request a fresh challenge to see the token and values again.
        </InlineAlert>
      )}

      <section className={styles.method} aria-labelledby="dv-dns-heading">
        <h2 id="dv-dns-heading" className={styles.methodHeading}>
          Option 1 — DNS TXT record
        </h2>
        <ReadOnlyField id="dv-dns-name" label="Record name (TXT)" value={recordName} />
        {issued ? (
          <ReadOnlyField id="dv-dns-value" label="Record value" value={issued.dnsTxt.value} />
        ) : null}
      </section>

      <section className={styles.method} aria-labelledby="dv-file-heading">
        <h2 id="dv-file-heading" className={styles.methodHeading}>
          Option 2 — HTTPS file
        </h2>
        <ReadOnlyField id="dv-file-url" label="File URL" value={fileUrl} />
        {issued ? (
          <ReadOnlyField id="dv-file-content" label="File content" value={issued.httpsFile.content} />
        ) : null}
      </section>

      <dl className={styles.meta}>
        <div className={styles.metaRow}>
          <dt className={styles.metaTerm}>Last checked</dt>
          <dd className={styles.metaValue}>{lastChecked ?? 'Not checked yet'}</dd>
        </div>
      </dl>

      {failureReason ? (
        <InlineAlert severity="warning">Last check: {failureReason}</InlineAlert>
      ) : null}

      <div className={styles.actions}>
        <Button busy={checking} onClick={onCheck}>
          {checking ? 'Checking…' : 'Check now'}
        </Button>
        <Button variant="secondary" busy={issuing} onClick={onIssue}>
          {issuing ? 'Re-requesting…' : 'Re-request'}
        </Button>
      </div>
    </div>
  );
}

interface ReadOnlyFieldProps {
  id: string;
  label: string;
  value: string;
  help?: string;
}

/** A labelled, read-only value with a copy-to-clipboard affordance. */
function ReadOnlyField({ id, label, value, help }: ReadOnlyFieldProps) {
  const [copied, setCopied] = useState(false);
  const helpId = help ? `${id}-help` : undefined;

  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be denied; the value remains selectable manually.
    }
  };

  return (
    <div className={styles.field}>
      <label className={styles.fieldLabel} htmlFor={id}>
        {label}
      </label>
      <div className={styles.fieldRow}>
        <input
          id={id}
          className={styles.fieldInput}
          type="text"
          value={value}
          readOnly
          {...(helpId ? { 'aria-describedby': helpId } : {})}
        />
        <Button
          variant="secondary"
          onClick={copy}
          aria-label={copied ? 'Copied' : `Copy ${label}`}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      {help ? (
        <span id={helpId} className={styles.fieldHelp}>
          {help}
        </span>
      ) : null}
    </div>
  );
}
